import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { DataService, CollectionQuery } from '../shared/sl-data/data.service';
import { DataPathsService } from '../shared/sl-data/data-paths.service';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap, startWith } from 'rxjs/operators';
import { FormBuilder, FormGroup } from '@angular/forms';
import moment from 'moment';
import firebase from 'firebase/compat/app';
import { DataEnvService } from '../shared/sl-data/data-env.service';

interface MultichannelMessage {
    id: string;
    jobId: number;
    content: string;
    author_type: 'system' | 'dispatcher' | 'client' | 'driver';
    receiver_phone: string;
    sender_phone: string;
    sender_method: 'call' | 'sms';
    type: 'regular' | 'customer_confirmation' | 'schedule';
}

@Component({
  selector: 'app-usage',
  templateUrl: './usage.component.html',
  styleUrls: ['./usage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UsageComponent implements OnInit {
  dateRangeForm: FormGroup;
  totalSmsSent$: Observable<number>;
  totalSmsReceived$: Observable<number>;

  constructor(
    private dataService: DataService,
    private dataPaths: DataPathsService,
    private fb: FormBuilder,
    private env: DataEnvService,
  ) {
    const startOfMonth = moment().startOf('month').toDate();
    const now = new Date();

    this.dateRangeForm = this.fb.group({
      dateFrom: [startOfMonth],
      dateTo: [now]
    });
  }

  ngOnInit(): void {
    this.env.setComFromCurrentUser();
    // Create observables that react to date range changes
    const dateFrom$ = this.dateRangeForm.get('dateFrom')!.valueChanges.pipe(
      startWith(this.dateRangeForm.get('dateFrom')!.value)
    );
    const dateTo$ = this.dateRangeForm.get('dateTo')!.valueChanges.pipe(
      startWith(this.dateRangeForm.get('dateTo')!.value)
    );

    // Combine date range with path to create queries
    this.totalSmsSent$ = combineLatest([
      this.dataPaths.multichannelMessagesOut$,
      dateFrom$,
      dateTo$
    ]).pipe(
      switchMap(([path, dateFrom, dateTo]) => {
        const queries: CollectionQuery[] = [
          ['sender_method', '==', 'sms']
        ];

        // Convert JavaScript Date to Firestore Timestamp for querying
        if (dateFrom) {
          queries.push(['created_at', '>=', firebase.firestore.Timestamp.fromDate(dateFrom)]);
        }
        if (dateTo) {
          queries.push(['created_at', '<=', firebase.firestore.Timestamp.fromDate(dateTo)]);
        }

        return this.dataService.getCollection$<MultichannelMessage>({
          path,
          queries
        });
      }),
      map(messages => messages.length)
    );

    this.totalSmsReceived$ = combineLatest([
      this.dataPaths.multichannelMessagesIn$,
      dateFrom$,
      dateTo$
    ]).pipe(
      switchMap(([path, dateFrom, dateTo]) => {
        const queries: CollectionQuery[] = [
          ['sender_method', '==', 'sms']
        ];

        if (dateFrom) {
          queries.push(['created_at', '>=', firebase.firestore.Timestamp.fromDate(dateFrom)]);
        }
        if (dateTo) {
          queries.push(['created_at', '<=', firebase.firestore.Timestamp.fromDate(dateTo)]);
        }

        return this.dataService.getCollection$<MultichannelMessage>({
          path,
          queries
        });
      }),
      map(messages => messages.length)
    );
  }
}

